import { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@app/store";
import { ensureMaxLightness, ensureMinLightness, withAlpha } from "@app/shared/utils/colorAlpha";

const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

// Foreground role (text/icons/pins): floor lightness so an arbitrary tenant
// hex stays legible once the surface behind it goes dark.
const FOREGROUND_MIN_LIGHTNESS = 0.55;
// Background role (opaque pastel fill, e.g. Button's secondary variant):
// ceiling lightness so it becomes a dark tint instead of staying near-white.
const BACKGROUND_MAX_LIGHTNESS = 0.22;

/**
 * Pushes the tenant's API-provided branding colors (`state.setting.colors`,
 * populated from `WebAppInitialSetting`/the track-order response — see
 * `store/setting.slice.ts`) onto `:root` as CSS custom properties,
 * overriding tokens.css's static fallback values. This is the one seam
 * where branding data crosses from Redux into the CSS-variable-driven
 * design system — every component that reads `var(--color-primary*)` /
 * `var(--color-icon-*)` picks the tenant's colors up automatically, with no
 * per-component wiring. Renders nothing.
 *
 * Dark mode: `prefers-color-scheme` can't be read from a plain inline style
 * (unlike tokens.css's own `@media` block for neutrals), so this listens
 * for it directly and re-derives each branding color with a lightness
 * clamp — brightened for foreground roles, darkened for the one opaque
 * light-background role — rather than requiring a hand-maintained second
 * palette per tenant. See `colorAlpha.ts`.
 */
export const BrandingColors = () => {
  const colors = useSelector((state: RootState) => state.setting.colors);
  const [prefersDark, setPrefersDark] = useState(() => window.matchMedia(DARK_MODE_QUERY).matches);

  useLayoutEffect(() => {
    const mql = window.matchMedia(DARK_MODE_QUERY);
    const handleChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  useLayoutEffect(() => {
    const fg = (hex: string) => (prefersDark ? ensureMinLightness(hex, FOREGROUND_MIN_LIGHTNESS) : hex);
    const bg = (hex: string) => (prefersDark ? ensureMaxLightness(hex, BACKGROUND_MAX_LIGHTNESS) : hex);

    const primary = fg(colors.primary);
    const iconDark = fg(colors.iconDarkStart);

    const root = document.documentElement.style;
    root.setProperty("--color-primary", primary);
    root.setProperty("--color-primary-dark", fg(colors.primaryDark));
    root.setProperty("--color-primary-light", fg(colors.primaryLight));
    root.setProperty("--color-primary-ultralight", bg(colors.primaryUltraLight));
    root.setProperty("--color-primary-on-light", fg(colors.primaryOnLight));
    root.setProperty("--color-primary-subtle", withAlpha(primary, 0.08));
    root.setProperty("--color-primary-subtle-border", withAlpha(primary, 0.2));
    root.setProperty("--color-icon-dark", iconDark);
    root.setProperty("--color-icon-light", fg(colors.iconLightStart));
  }, [colors, prefersDark]);

  return null;
};
